/**
 * @file DetailsTab.js
 * @description Component for rendering the details tab of the message page.
 **/
import { useState, useEffect } from 'react';
import { CharacterLimitIndicator } from '../helpers/CharacterLimitIndicator';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import { Dropdown } from '../helpers/DropdownSearchableSingleSelect';
import Spinner from '../ui/spinners/Spinner';
import styles from './Messages.module.scss';
import { formatDateToMmmDDYYYY } from '../helpers/DateUtils';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import { getText } from '../../i18n';

/**
 * Renders a form for displaying and editing message details.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - The initial data for the form.
 * @param {Function} props.onChange - The callback function to be called when the form data changes.
 * @returns {JSX.Element} The rendered DetailsTab component.
 */
const DetailsTab = ({
    data,
    onChange,
    isCreating,
    user,
    useWideLayout = false,
}) => {
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
    const [formData, setFormData] = useState(data);
    const [populationGroups, setPopulationGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [userDefaultGroup, setUserDefaultGroup] = useState(null);

    useEffect(() => {
        const loadPopulationGroups = async () => {
            try {
                const response = await fetchPopulationGroups();
                const defaultGroupId = isAdminAudienceEnabled
                    ? user?.jwt?.df
                    : null;
                const defaultGroup = response.find(
                    group => group.id === defaultGroupId,
                );
                setUserDefaultGroup(defaultGroup || null);

                const filtered = defaultGroupId
                    ? response.filter(group => group.id !== defaultGroupId)
                    : response;
                setPopulationGroups(filtered);
            } catch (error) {
                console.error('Error fetching population groups:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadPopulationGroups();
    }, [isAdminAudienceEnabled, user?.jwt?.df]);

    useEffect(() => {
        if (Object.keys(formData).length === 0) {
            setFormData(data);
        }
    }, [data]);

    useEffect(() => {
        if (JSON.stringify(formData) !== JSON.stringify(data)) {
            onChange(formData);
        }
    }, [formData, onChange]);

    const handleNotificationCategoryChange = categoryId => {
        setFormData(prevDetails => ({
            ...prevDetails,
            notificationCategoryId: categoryId,
        }));
    };

    const handleOnChangeName = event => {
        setFormData(prevDetails => ({
            ...prevDetails,
            name: event.target.value,
        }));
    };

    const notificationCategoriesList = [
        { value: 1, label: getText('msg_1c28e6ed2989') },
        { value: 2, label: getText('msg_89fd42a02dab') },
        { value: 3, label: getText('msg_a6287a000d9c') },
        { value: 4, label: getText('msg_cbb591c6e50a') },
        { value: 5, label: getText('msg_bb360d43ab9d') },
        { value: 6, label: getText('msg_5a957bb79f78') },
        { value: 7, label: getText('msg_4b9b7a37775c') },
        { value: 8, label: getText('msg_2c8f7e5f8b0d') },
        { value: 9, label: getText('msg_9b70d325f251') },
        { value: 10, label: getText('msg_9433d727e687') },
        { value: 11, label: getText('msg_d4357eced936') },
        { value: 12, label: getText('msg_d82f4aada875') },
        { value: 13, label: getText('msg_b8ec3422648f') },
        { value: 14, label: getText('msg_a6a5df2a218f') },
        { value: 15, label: getText('msg_6e6a6f2086bb') },
        { value: 16, label: getText('msg_bc0792d8dc81') },
    ];

    const getDefaultLabel = () => {
        const matchingCategory = notificationCategoriesList.find(
            category => category.value === data.notificationCategoryId,
        );
        return matchingCategory ? matchingCategory.label : 'Select';
    };

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <div
            className={
                useWideLayout
                    ? 'col-12 col-xs-12 col-md-12 col-lg-12 pb-5'
                    : 'col-12 col-xs-12 col-md-12 col-lg-10 m-auto pb-5'
            }
        >
            <div className="row g-4">
                <div className="col-12 col-lg-8">
                    <div className="row g-4">
                        <div className="col-12 col-lg-7">
                            <div className="file-form-label">
                                {getText('msg_709a23220f2c')}
                                <span
                                    style={{
                                        color: '#EF4E45',
                                        marginLeft: '4px',
                                        fontWeight: '500',
                                    }}
                                >
                                    *
                                </span>
                            </div>
                            <input
                                onChange={handleOnChangeName}
                                defaultValue={data.name}
                                style={{
                                    height: '48px',
                                    borderRadius: '12px',
                                    border: 'solid 1px rgba(47,43, 67, 0.2)',
                                    paddingLeft: '16px',
                                    paddingRight: '16px',
                                }}
                                className="form-control"
                                type="text"
                                placeholder={getText('msg_5df85959e93e')}
                                aria-label={getText('msg_8ece909b4754')}
                            />
                            <CharacterLimitIndicator
                                maxChars={50}
                                inputStr={data.name || ''}
                            />
                        </div>

                        <div className="col-12 col-lg-5">
                            <div className="file-form-label">
                                {getText('msg_a3c686e711e4')}
                                <span
                                    style={{
                                        color: '#EF4E45',
                                        marginLeft: '4px',
                                        fontWeight: '500',
                                    }}
                                >
                                    *
                                </span>
                            </div>

                            <div className={styles.alignedDropdown}>
                                <Dropdown
                                    id="notification-category-dropdown"
                                    defaultValue={getDefaultLabel()}
                                    options={[
                                        ...notificationCategoriesList,
                                    ].sort((a, b) =>
                                        a.label.localeCompare(b.label),
                                    )}
                                    value={data.notificationCategoryId}
                                    onChange={handleNotificationCategoryChange}
                                    disabled={false}
                                    allowSearch={false}
                                    useStandardFormStyle={true}
                                />
                            </div>

                            <div className={styles.helperSpacer} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailsTab;
